import type { AppRouterOutputs } from '@alison/api/router';
import { Button } from '@alison/ui';
import EventRepeatOutlined from '@mui/icons-material/EventRepeatOutlined';
import MoreHorizOutlined from '@mui/icons-material/MoreHorizOutlined';
import OpenInNewOutlined from '@mui/icons-material/OpenInNewOutlined';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { TRPCClientError } from '@trpc/client';
import { type ReactNode, useState } from 'react';
import {
  useAutomation,
  useAutomations,
  useDeleteAutomation,
  useRunAutomationNow,
  useSetAutomationStatus,
  useUpdateAutomation,
} from '../../../lib/automations.hooks.js';
import { useWorkflowCatalog } from '../../../lib/workflows.hooks.js';
import { humanize } from '../catalogFilters.js';
import type { OpenedRun } from '../runs/RunsTab.js';
import {
  AutomationDrawer,
  type AutomationForm,
  type AutomationSubmit,
} from './AutomationDrawer.js';
import { runAtLabel, scheduleLabel } from './automationText.js';
import { DeliveryLogDialog } from './DeliveryLogDialog.js';

const HEADINGS = [
  'Automation',
  'Schedule',
  'Next run',
  'Last run',
  'Outcomes',
  'Owner',
  'Created',
  'Status',
  'Action',
];

const HEAD_SX = {
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase',
  color: 'text.disabled',
};

const ERRORS: Record<string, string> = {
  automation_cap: 'Your organization has reached its limit of active automations.',
  automation_running: 'This automation is already running.',
  automation_not_active: 'Resume the automation before running it.',
  schedule_ended: 'This schedule has no runs left. Change when it ends first.',
};

type Row = AppRouterOutputs['automations']['list'][number];
type Automation = Row['automation'];
type Status = Automation['status'];

interface Filters {
  search: string;
  workflow: string;
  status: Status | '';
  owner: string;
}

interface EditProps {
  id: string;
  onClose: () => void;
}

interface AutomationsTabProps {
  onOpenRun: (run: OpenedRun) => void;
}

function errorText(error: unknown): string {
  const message = error instanceof TRPCClientError ? error.message : '';
  return ERRORS[message] ?? 'Something went wrong. Try again.';
}

function workflowOf(automation: Automation): string {
  const [first] = automation.steps as { action_type: string }[];
  return first?.action_type ?? '';
}

function dateLabel(at: string | Date): string {
  return new Date(at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function formOf(automation: Automation): AutomationForm {
  return {
    name: automation.name,
    frequency: automation.frequency,
    timeOfDay: automation.timeOfDay,
    ends: automation.ends,
    endDate: automation.endDate,
    maxRuns: automation.maxRuns,
    emailRecipients: automation.emailRecipients,
    slackChannelId: automation.slackChannelId,
    onlyWhenFound: automation.onlyWhenFound,
  };
}

function matches(row: Row, filters: Filters): boolean {
  const { automation } = row;
  return (
    automation.name.toLowerCase().includes(filters.search.trim().toLowerCase()) &&
    (filters.workflow === '' || workflowOf(automation) === filters.workflow) &&
    (filters.status === '' || automation.status === filters.status) &&
    (filters.owner === '' || String(automation.ownerUserId) === filters.owner)
  );
}

function EditAutomation({ id, onClose }: EditProps): ReactNode {
  const found = useAutomation(id);
  const update = useUpdateAutomation();
  if (found.isError) {
    return (
      <Dialog open onClose={onClose}>
        <DialogContent>
          <Alert severity="error">The automation could not be loaded.</Alert>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={onClose}>
            Close
          </Button>
          <Button variant="outlined" onClick={() => void found.refetch()}>
            Try again
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
  // The form snapshots its first value, so it waits for this open's fetch, not a cached copy.
  if (found.data === undefined || !found.isFetchedAfterMount) return null;
  const { automation, steps } = found.data;
  const accounts = automation.accountIds.length;

  const handleSave = ({ schedule, picks }: AutomationSubmit): void =>
    update.mutate({ id, schedule, picks }, { onSuccess: onClose });

  return (
    <AutomationDrawer
      open
      mode="edit"
      steps={steps}
      scopeLine={`${accounts} ${accounts === 1 ? 'account' : 'accounts'} · last ${automation.windowDays} days`}
      initial={formOf(automation)}
      saving={update.isPending}
      notice={update.isError ? <Alert severity="error">{errorText(update.error)}</Alert> : null}
      onSave={handleSave}
      onClose={onClose}
    />
  );
}

export function AutomationsTab({ onOpenRun }: AutomationsTabProps): ReactNode {
  const list = useAutomations({});
  const catalog = useWorkflowCatalog();
  const setStatus = useSetAutomationStatus();
  const runNow = useRunAutomationNow();
  const remove = useDeleteAutomation();
  const [filters, setFilters] = useState<Filters>({
    search: '',
    workflow: '',
    status: '',
    owner: '',
  });
  const [menu, setMenu] = useState<{ anchor: HTMLElement; row: Row } | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Row | null>(null);
  const [logOf, setLogOf] = useState<Automation | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const names = new Map(
    (catalog.data?.groups ?? []).flatMap((group) =>
      group.items.map((card): [string, string] => [card.workflowId, card.name]),
    ),
  );
  const rows = list.data ?? [];
  const shown = rows.filter((row) => matches(row, filters));
  const owners = new Map(rows.map((row) => [String(row.automation.ownerUserId), row.ownerName]));
  const workflows = [...new Set(rows.map((row) => workflowOf(row.automation)))];
  const nameOf = (actionType: string): string => names.get(actionType) ?? humanize(actionType);
  const onError = (error: unknown): void => setProblem(errorText(error));

  const handleStatus = (row: Row, status: 'active' | 'paused'): void => {
    setProblem(null);
    setStatus.mutate({ id: row.automation.id, status }, { onError });
  };

  if (list.isPending) {
    return (
      <Stack sx={{ alignItems: 'center', py: 10 }}>
        <CircularProgress size={24} />
      </Stack>
    );
  }
  if (list.isError) return <Alert severity="error">Automations could not be loaded.</Alert>;

  return (
    <Stack sx={{ gap: 3 }}>
      <Typography sx={{ fontSize: 16, fontWeight: 600 }}>Automations ({rows.length})</Typography>

      <Stack direction="row" sx={{ gap: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search"
          value={filters.search}
          onChange={(event) => setFilters({ ...filters, search: event.target.value })}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined sx={{ fontSize: 16 }} />
                </InputAdornment>
              ),
            },
          }}
        />
        <TextField
          select
          size="small"
          label="Workflow"
          value={filters.workflow}
          onChange={(event) => setFilters({ ...filters, workflow: event.target.value })}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All workflows</MenuItem>
          {workflows.map((workflow) => (
            <MenuItem key={workflow} value={workflow}>
              {nameOf(workflow)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Status"
          value={filters.status}
          onChange={(event) =>
            setFilters({ ...filters, status: event.target.value as Status | '' })
          }
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">All statuses</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="paused">Paused</MenuItem>
          <MenuItem value="ended">Ended</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Owner"
          value={filters.owner}
          onChange={(event) => setFilters({ ...filters, owner: event.target.value })}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">All owners</MenuItem>
          {[...owners].map(([id, name]) => (
            <MenuItem key={id} value={id}>
              {name}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {problem === null ? null : (
        <Alert severity="error" onClose={() => setProblem(null)}>
          {problem}
        </Alert>
      )}

      {rows.length === 0 ? (
        <Stack sx={{ alignItems: 'center', gap: 1, py: 10 }}>
          <EventRepeatOutlined sx={{ color: 'text.disabled' }} />
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            No automations yet. Save a finished session as an automation from its session map.
          </Typography>
        </Stack>
      ) : (
        <Box
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflowX: 'auto' }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                {HEADINGS.map((heading) => (
                  <TableCell key={heading} sx={HEAD_SX}>
                    {heading}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map((row) => {
                const { automation, lastRun } = row;
                const chain = (automation.steps as { action_type: string }[])
                  .map((step) => nameOf(step.action_type))
                  .join(' → ');
                return (
                  <TableRow key={automation.id} data-testid={`automation-row-${automation.id}`}>
                    <TableCell>
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                        {automation.name}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                        {chain}
                      </Typography>
                      {row.slackUnavailable ? (
                        <Chip
                          size="small"
                          color="warning"
                          variant="outlined"
                          icon={<WarningAmberOutlined />}
                          label="Slack channel unavailable"
                          sx={{ mt: 0.5, fontSize: 11 }}
                        />
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {scheduleLabel(automation.frequency, automation.timeOfDay)}
                    </TableCell>
                    <TableCell>
                      {automation.nextRunAt === null ? '—' : runAtLabel(automation.nextRunAt)}
                    </TableCell>
                    <TableCell>
                      {lastRun === null
                        ? '—'
                        : `${humanize(lastRun.status)} · ${dateLabel(lastRun.startedAt)}`}
                    </TableCell>
                    <TableCell>
                      {lastRun?.rootRunId == null ? (
                        '—'
                      ) : (
                        <Link
                          component="button"
                          type="button"
                          onClick={() =>
                            onOpenRun({
                              runId: lastRun.rootRunId ?? '',
                              actionType: workflowOf(automation),
                              accountIds: lastRun.accountIds,
                              startDate: lastRun.startDate,
                              endDate: lastRun.endDate,
                            })
                          }
                          sx={{ display: 'inline-flex', gap: 0.5, alignItems: 'center' }}
                        >
                          Last run <OpenInNewOutlined sx={{ fontSize: 12 }} />
                        </Link>
                      )}
                    </TableCell>
                    <TableCell>{row.ownerName}</TableCell>
                    <TableCell>{dateLabel(automation.createdAt)}</TableCell>
                    <TableCell>
                      <Stack direction="row" sx={{ alignItems: 'center' }}>
                        <Switch
                          size="small"
                          checked={automation.status === 'active'}
                          disabled={automation.status === 'ended'}
                          onChange={(event) =>
                            handleStatus(row, event.target.checked ? 'active' : 'paused')
                          }
                          slotProps={{ input: { 'aria-label': `${automation.name} active` } }}
                        />
                        {humanize(automation.status)}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={automation.status !== 'active' || runNow.isPending}
                          onClick={() => {
                            setProblem(null);
                            runNow.mutate({ id: automation.id }, { onError });
                          }}
                        >
                          Run now
                        </Button>
                        <Link
                          component="button"
                          type="button"
                          onClick={() => setEditing(automation.id)}
                        >
                          Edit
                        </Link>
                        <IconButton
                          size="small"
                          aria-label={`More for ${automation.name}`}
                          onClick={(event) => setMenu({ anchor: event.currentTarget, row })}
                        >
                          <MoreHorizOutlined sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}

      <Menu anchorEl={menu?.anchor ?? null} open={menu !== null} onClose={() => setMenu(null)}>
        {menu?.row.automation.status === 'ended' ? null : (
          <MenuItem
            onClick={() => {
              if (menu !== null) {
                handleStatus(
                  menu.row,
                  menu.row.automation.status === 'active' ? 'paused' : 'active',
                );
              }
              setMenu(null);
            }}
          >
            {menu?.row.automation.status === 'active' ? 'Pause' : 'Resume'}
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setLogOf(menu?.row.automation ?? null);
            setMenu(null);
          }}
        >
          Delivery log
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDeleting(menu?.row ?? null);
            setMenu(null);
          }}
        >
          Delete
        </MenuItem>
      </Menu>

      <Dialog open={deleting !== null} onClose={() => setDeleting(null)}>
        <DialogTitle>Delete automation?</DialogTitle>
        <DialogContent>
          {deleting?.automation.name} stops running. Outcomes it already saved stay in Outcomes.
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setDeleting(null)}>
            Cancel
          </Button>
          <Button
            variant="gradient"
            disabled={remove.isPending}
            onClick={() => {
              if (deleting === null) return;
              setProblem(null);
              remove.mutate(
                { id: deleting.automation.id },
                { onSuccess: () => setDeleting(null), onError },
              );
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {editing === null ? null : <EditAutomation id={editing} onClose={() => setEditing(null)} />}
      {logOf === null ? null : (
        <DeliveryLogDialog automation={logOf} onClose={() => setLogOf(null)} />
      )}
    </Stack>
  );
}
